import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import { isEmpty } from 'lodash'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { VariableSizeList as List } from 'react-window'

import { NotificationItem } from '../../components'
import { t } from 'i18n'

const LIST_MAX_HEIGHT = 560
const ROW_HEIGHT = 140

export default function NotificationsComponent(props) {
  const {
    notifications,
    notificationsAreLoading,
    markAllNotificationsAsReadMutation,
    notificationsMenuState
  } = props
  const listRef = useRef<List>(null)
  const sizeMap = useRef<Record<number, number>>({})
  const isOpen = Boolean(notificationsMenuState?.isOpen)

  const getSize = (index: number) => sizeMap.current[index] ?? ROW_HEIGHT

  const setSize = (index: number, size: number) => {
    if (sizeMap.current[index] === size) return
    sizeMap.current[index] = size
    listRef.current?.resetAfterIndex(index)
  }

  useEffect(() => {
    if (!isOpen) return
    sizeMap.current = {}
    listRef.current?.resetAfterIndex(0, true)
  }, [isOpen])

  useEffect(() => {
    sizeMap.current = {}
    listRef.current?.resetAfterIndex(0, true)
  }, [notifications.length])

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper elevation={4} style={{ width: '100%' }}>
          <Box p={2} width={1}>
            <Grid container justifyContent='space-between' alignItems='center'>
              <Typography variant='h6' fontWeight={600} color='text.primary'>
                {t('notifications.yourNotifications')}
              </Typography>

              {notifications.find(notification => !notification.read) && (
                <Button
                  color='primary'
                  size='small'
                  onClick={markAllNotificationsAsReadMutation.mutate}
                >
                  {t('notifications.markAllRead')}
                </Button>
              )}
            </Grid>
          </Box>

          <Grid container>
            {notificationsAreLoading ? (
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                sx={{ mt: 3, mb: 5 }}
              >
                <CircularProgress size={40} />
              </Grid>
            ) : isEmpty(notifications) ? (
              <Box p={2} pt={0}>
                <Typography variant='body2' color='text.secondary' paragraph>
                  {t('notifications.noNotifications')}
                </Typography>
              </Box>
            ) : (
              <List
                ref={listRef}
                height={LIST_MAX_HEIGHT}
                itemCount={notifications.length}
                itemSize={getSize}
                width="100%"
                overscanCount={6}
                itemKey={(index) => notifications[index]?.id ?? index}
              >
                {({ index, style }) => {
                  const notification = notifications[index]

                  return (
                    <Row
                      index={index}
                      style={style}
                      setSize={setSize}
                      isOpen={isOpen}
                    >
                      <NotificationItem
                        {...props}
                        key={notification.id}
                        notification={notification}
                        notificationsMenuState={notificationsMenuState}
                      />
                    </Row>
                  )
                }}
              </List>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

function Row({
  index,
  style,
  setSize,
  isOpen,
  children,
}: {
  index: number
  style: React.CSSProperties
  setSize: (index: number, size: number) => void
  isOpen: boolean
  children: React.ReactNode
}) {
  const rowRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) return
    if (!rowRef.current) return
    const height = rowRef.current.getBoundingClientRect().height
    setSize(index, Math.ceil(height))
  }, [index, isOpen, setSize])

  useEffect(() => {
    if (!isOpen) return
    if (!rowRef.current || typeof ResizeObserver === 'undefined') return
    const element = rowRef.current
    const observer = new ResizeObserver(() => {
      const height = element.getBoundingClientRect().height
      setSize(index, Math.ceil(height))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [index, isOpen, setSize])

  return (
    <div style={style}>
      <div ref={rowRef} style={{ width: '100%', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  )
}
